import moment from 'moment'

import * as dataGrid from '@/composables/data-grid'
import CustomStore from 'devextreme/data/custom_store'

import { ref, computed } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { useLookup, useCurrencyOptions, useCurrencyColumnFormatter, usePercentageOptions, useODataSource } from '@/composables/data-grid'
import { useValidation } from '@/composables/validation'
import { useGetOrFetchAsync } from '@/composables/store'
import { useDocumentIdentifiers } from '@/composables/document'
import { codeNameDisplayExpr } from '@/composables/label'

import { useProductService } from '@/services/product.service'
import { useSubjectService } from '@/services/subject.service'
import { useUnitOfMeasureService } from '@/services/unit.of.measure.service'

import { useGarageStore } from '@/stores/garage.store'
import { useOrderStore } from '@/stores/order.store'
import { usePortalStore } from '@/stores/portal.store'
import { useRegistryStore } from '@/stores/registry.store'
import { useSaleStore } from '@/stores/sale.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { isNullOrUndefined, fixedDecimal } from '@/lib/utils'

const addressValidation = (field = 'customer') => {
  const i18n = getI18n()

  return {
    type: 'custom',
    message: i18n.t('common.required'),
    reevaluate: true,
    validationCallback: (e) => {
      const address = e.data[field]?.address || {}

      const fields = [address.country, address.state, address.state_text, address.city, address.city_text, address.street, address.zip_code]

      const allEmpty = fields.every(v => !v)
      if (allEmpty) return true

      if (e.column.name?.includes('delivery_address')) return true

      const effectiveCity = address.city || address.city_text
      const effectiveState = address.state || address.state_text

      if (e.column.name?.includes('city')) return !!effectiveCity
      if (e.column.name?.includes('state')) return !!effectiveState

      return e.value != null && e.value !== ''
    },
  }
}

const setByPath = (obj, path, value) => {
  const keys = path.split('.')
  const lastKey = keys.pop()

  const target = keys.reduce((acc, key) => {
    acc[key] ??= {}
    return acc[key]
  }, obj)

  target[lastKey] = value
}

export const useDocumentSeriesAndNumberMnemonicUpdate = () => {
  const { updateSeriesAndNumberMnemonicAsync } = useDocumentIdentifiers()

  const withDocumentSeriesAndNumberMnemonicUpdate = (setCellValueFn, trackedDocumentIdFields, editors, api) => {
    return async(newData, value, currentData) => {
      await setCellValueFn(newData, value, currentData)
      if (trackedDocumentIdFields.some(f => f in newData)) {
        const updatedData = { ...currentData, ...newData }
        await updateSeriesAndNumberMnemonicAsync(updatedData, editors, api)
      }
    }
  }

  return { withDocumentSeriesAndNumberMnemonicUpdate }
}

export const useAttachmentColumn = (options) => {
  const webSiteStore = useWebSiteStore()
  const i18n = getI18n()

  return {
    ...{
      dataField: 'attachments',
      caption: webSiteStore.blankCaption,
      cellTemplate: 'attachmentsCellTemplate',
      width: '4rem',
      lookup: dataGrid.useLookup({ dataSource: {
        store: [{ id: true, name: i18n.t('common.yes') }, { id: false, name: i18n.t('common.no') }],
      } }),
      calculateFilterExpression: (filterValue) => { return ['has_attachments', '=', filterValue] },
    }, ...options
  }
}

export const useButtonsInfoTooltipColumn = (options) => {
  return {
    ...{
      name: 'infoTooltip',
      location: 'after',
      options: {
        stylingMode: 'text',
        template: 'fieldInfoTooltipTemplate',
        hoverStateEnabled: false,
        focusStateEnabled: false,
        activeStateEnabled: false,
      },
    }, ...options
  }
}

export const useAddressRef = (options) => {
  const webSiteStore = useWebSiteStore()

  const currentCountryId = ref(null)
  const currentStateId = ref(null)
  const currentVatCountryId = ref(null)
  const currentDeliveryCountryId = ref(null)
  const currentDeliveryStateId = ref(null)
  const currentZipCode = ref(null)
  const currentFilteredCitiesCount = ref(null)
  const currentDeliveryZipCode = ref(null)
  const currentDeliveryFilteredCitiesCount = ref(null)

  const defaultType = 'customer'

  const getRefsByType = (type) => {
    switch (type) {
      case 'delivery_address':
        return { country: currentDeliveryCountryId, state: currentDeliveryStateId }
      case 'vat_country':
        return { country: currentVatCountryId, state: null }
      default:
        return { country: currentCountryId, state: currentStateId }
    }
  }

  const onCountryChanged = (e, dataField = null, type = defaultType) => {
    const { country } = getRefsByType(type)
    country.value = e?.value?.id ?? null
    currentZipCode.value = null
    if (dataField === 'vat_country_id' || dataField === 'customer.vat_country_id') {
      options?.onVatCountryChanged?.()
    }
  }

  const onStateChanged = (e, type = defaultType) => {
    const { state } = getRefsByType(type)
    currentZipCode.value = null
    state.value = e?.value?.id ?? null
  }

  const setCellValueCountry = (newData, value, currentData, type = defaultType) => {
    const { country, state } = getRefsByType(type)
    newData[type] = {}
    newData[type].address = {}
    newData[type].address.country = country.value = value?.id
    newData[type].address.state = state.value = null
    newData[type].address.city = null
    newData[type].address.zip_code = currentZipCode.value = currentDeliveryZipCode.value = null
    newData[type].address.state_text = newData[type].address.city_text = null
    newData[type].address.street = null
  }

  const setCellValueState = (newData, value, currentData, type = defaultType) => {
    const { state } = getRefsByType(type)
    newData[type] ||= {}
    newData[type].address ||= {}

    const actualAddress = {...currentData[type].address, ...newData[type].address}

    const currentCountry = webSiteStore.countries.find(x => x.id === actualAddress?.country)
    if (currentCountry?.has_structured_location_data) {
      newData[type].address.state = state.value = value?.id
    } else {
      newData[type].address.state_text = value
    }
  }

  const setCellValueCity = (newData, value, currentData, type = defaultType) => {
    newData[type] ||= {}
    newData[type].address ||= {}

    newData[type].address.city = null
    newData[type].address.city_text = null

    const actualAddress = {...currentData?.[type]?.address, ...newData[type].address}

    const currentCountry = webSiteStore.countries.find(x => x.id === actualAddress?.country)

    const config = type === 'delivery_address' ? {
      filteredCitiesCount: currentDeliveryFilteredCitiesCount,
      zipCodeRef: currentDeliveryZipCode
    } : {
      filteredCitiesCount: currentFilteredCitiesCount,
      zipCodeRef: currentZipCode
    }

    const canUseStructuredCity = currentCountry?.has_structured_location_data && (config.filteredCitiesCount.value == null || config.filteredCitiesCount.value > 0)

    if (canUseStructuredCity) {
      newData[type].address.state = value?.state
      newData[type].address.country = value?.country
      newData[type].address.zip_code = config.zipCodeRef.value = value?.zip_code
      newData[type].address.city = value?.id
    } else {
      newData[type].address.city_text = value
    }
  }

  const resolveAddressTarget = (newData, value, currentData, type, isRootAddress) => {
    if (isRootAddress) {
      newData.address ??= {}
      newData.address.country = currentData?.address?.country
      newData.address.zip_code = value
      return newData.address
    }

    newData[type] ??= currentData?.[type] ?? {}
    newData[type].address ??= currentData?.[type]?.address ?? {}

    newData[type].address.country = currentData?.[type]?.address?.country
    newData[type].address.zip_code = value

    return newData[type].address
  }

  const resetAddressFields = (address, stateRef) => {
    address.city =
    address.city_text =
    address.state_text =
    address.street =
    address.state =
    stateRef.value = null
  }

  const setCellValueZipCode = async(newData, value, currentData, type = defaultType, flat = true) => {
    const { country, state } = getRefsByType(type)

    const address = resolveAddressTarget(newData, value, currentData, type, flat)

    address.country = country.value
    resetAddressFields(address, state)

    const cities = ref([])
    const filteredCities = await useGetOrFetchAsync(cities, () => webSiteStore.api.getCitiesAsync({zip_code: value, country: country.value}), false)

    if (type === 'delivery_address') {
      currentDeliveryFilteredCitiesCount.value = filteredCities.length
    } else {
      currentFilteredCitiesCount.value = filteredCities.length
    }

    if (filteredCities.length === 1) {
      address.city = filteredCities[0].id
      address.state = state.value = filteredCities[0].state
    }

    if (type === 'delivery_address') {
      currentDeliveryZipCode.value = value
    } else {
      currentZipCode.value = value
    }
  }

  return { currentCountryId, currentStateId, currentVatCountryId, currentDeliveryCountryId, currentDeliveryStateId, currentZipCode, currentDeliveryZipCode, currentFilteredCitiesCount, currentDeliveryFilteredCitiesCount, onCountryChanged, onStateChanged, setCellValueState, setCellValueCity, setCellValueCountry, setCellValueZipCode }
}

export const useProductAvailabilityColumn = (options) => {
  const i18n = getI18n()
  const registryStore = useRegistryStore()

  const defaultOptions = {
    caption: i18n.t('common.productAvailability'),
    dataField: 'product_availability',
    lookup: useLookup({ store: registryStore.getLocalizedProductAvailabilities() }),
    width: '6rem',
  }

  return {...defaultOptions, ...options }
}

export const useChargePercentageColumn = (options) => {
  const i18n = getI18n()
  const { setChargePercentageValue } = useProductService()

  const defaultOptions = {
    caption: i18n.t('common.chargePercentage'),
    ...usePercentageOptions(),
    dataField: 'charge_percentage',
    dataType: 'number',
    visible: false,
    editorOptions: { min: 0 },
    setCellValue: (newData, value, currentRowData) => {
      setChargePercentageValue(newData, value, currentRowData)
    }
  }

  return { ...defaultOptions, ...options }
}

export const useCostValorizationColumn = (options = {}) => {
  const i18n = getI18n()
  const garageStore = useGarageStore()
  const webSiteStore = useWebSiteStore()
  const { setCostValorizationValueAsync } = useProductService()
  const { priceContext = {}, ...columnOptions } = options

  return {...{
    caption: i18n.t('common.costValorization'),
    dataField: 'cost_valorization',
    lookup: useLookup({
      dataSource: {
        store: garageStore.getLocalizedCostValorizations(),
        sort: 'name',
      },
      displayExpr: 'name',
    }
    ),
    setCellValue: async(newData, value, currentRowData) => {
      await setCostValorizationValueAsync(newData, value, currentRowData, priceContext)
    },
    validationRules: [
      {
        type: 'custom',
        message: i18n.t('common.required'),
        reevaluate: true,
        validationCallback: (e) => {
          return !isNullOrUndefined(e.value) || e.data.document_row_type === webSiteStore.documentRowTypesEnum.TEXT
        }
      }
    ]
  }, ...columnOptions}
}

export const useCurrencyColumn = (options) => {
  const i18n = getI18n()
  const webSiteStore = useWebSiteStore()

  return {...{
    caption: i18n.t('common.currency'),
    dataField: 'currency_ref',
    lookup: useLookup({ store: webSiteStore.getLocalizedCurrencies() }),
    setCellValue: (newData, value) => {
      newData.currency_ref = value
      newData.exchange_rate = null
      newData.exchange_rate_date = null
      const currentCurrency = webSiteStore.currencies.find((currency) => currency.id === value)
      if (currentCurrency) {
        newData.exchange_rate = currentCurrency.exchange_rate
        newData.exchange_rate_date = currentCurrency.exchange_rate_date
        if (options?.editors) {
          options.editors['paid_amount']?.option('format', useCurrencyOptions({ currency: currentCurrency }).format)
        }
      }
    },
    visible: false,
    validationRules: [{ type: 'required' }],
  }, ...options}
}

export const useCustomerAddressCityColumn = (options) => {
  const i18n = getI18n()
  const dataField = options?.dataField || 'customer.address.city'

  return {...{
    caption: options?.caption || i18n.t('common.cityAddress'),
    dataField: dataField,
    editCellTemplate: 'cityEditor',
    visible: false,
    showInColumnChooser: false,
    validationRules: [addressValidation(options?.field)],
  }, ...options }
}

export const useCustomerAddressCountryColumn = (options) => {
  const i18n = getI18n()
  const dataField = options?.dataField || 'customer.address.country'

  return {...{
    caption: options?.caption || i18n.t('common.country'),
    dataField: dataField,
    editCellTemplate: 'countryEditor',
    visible: false,
    showInColumnChooser: false,
    validationRules: [addressValidation(options?.field)],
  }, ...options }
}

export const useCustomerAddressStreetColumn = (options) => {
  const i18n = getI18n()
  const dataField = options?.dataField || 'customer.address.street'

  return {...{
    caption: options?.caption || `${i18n.t('common.customer')} ${i18n.t('common.street')}`,
    dataField: dataField,
    visible: false,
    showInColumnChooser: false,
    validationRules: [addressValidation(options?.field)],
  }, ...options }
}

export const useCustomerAddressZipCodeColumn = (options) => {
  const i18n = getI18n()
  const dataField = options?.dataField || 'customer.address.zip_code'

  return {...{
    caption: options?.caption || `${i18n.t('common.customer')} ${i18n.t('common.zipCode')}`,
    dataField: dataField,
    visible: false,
    showInColumnChooser: false,
    validationRules: [addressValidation(options?.field)],
    setCellValue: (newData, value, currentData) => options.setCellValueZipCode(newData, value, currentData, null)
  }, ...options }
}

export const useCustomerAddressStateColumn = (options) => {
  const i18n = getI18n()

  const dataField = options?.dataField || 'customer.address.state'

  return {...{
    caption: options?.caption || i18n.t('common.customer'),
    dataField: dataField,
    editCellTemplate: 'stateEditor',
    visible: false,
    showInColumnChooser: false,
    validationRules: [addressValidation(options?.field)],
  }, ...options }
}

export const useCustomerColumn = (options) => {
  const i18n = getI18n()
  const registryStore = useRegistryStore()

  const dataField = options?.dataField || 'customer'

  return {...{
    caption: options?.caption || i18n.t('common.customer'),
    dataField: dataField,
    lookup: useLookup({
      dataSource: {
        store: registryStore.customers,
        sort: 'code',
        paginate: true,
      },
    }),
    setCellValue: async(newData, value) => {
      const id = value?.id || value
      newData[dataField || 'customer'] = id
    },
    validationRules: [{ type: 'required' }]
  }, ...options }
}

export const useCustomerMobileColumn = (options) => {
  const i18n = getI18n()

  return {...{
    dataField: 'customer.mobile',
    caption: `${i18n.t('common.customer')} ${i18n.t('common.mobile')}`,
    validationRules: [{ type: 'required' }],
    visible: false,
    showInColumnChooser: false,
  }, ...options }
}

export const useDeliveryAddressColumn = (options) => {
  const i18n = getI18n()
  const registryStore = useRegistryStore()

  const getFilteredSubjectContacts = () => {
    const store = registryStore.subjectContacts.filter(i => options?.onlyCustomer ? !i.is_portal : true).map(i => {
      if (options?.onlyCustomer) { return { ...i, id: Math.abs(i.id) } }
      return i
    })
    return {
      store: store,
      sort: 'code',
      paginate: true,
    }
  }
  const dataField = options?.dataField || 'delivery_address'

  const defaultOptions = {
    dataField: dataField,
    lookup: useLookup({
      dataSource: getFilteredSubjectContacts,
      codeOnly: options?.codeOnly,
    })
  }

  options = { ...defaultOptions, ...options }

  return {...{
    caption: i18n.t('common.deliveryAddress'),
    setCellValue: async(newData, value) => {
      const id = value?.id || value
      newData[dataField || 'delivery_address'] = id
    },
    validationRules: [{ type: 'required' }]
  }, ... defaultOptions, ...options }
}

export const useDepositColumn = (options) => {
  const i18n = getI18n()
  const registryStore = useRegistryStore()

  const dataField = options?.dataField || 'deposit'

  return {...{
    dataField: dataField,
    editCellTemplate: 'depositEditor',
    caption: i18n.t('common.deposit'),
    lookup: useLookup({ store: registryStore.getLocalizedDeposits() }),
    setCellValue: (newData, value) => {
      newData[dataField] = value?.id
    },
    visible: false,
    validationRules: [{ type: 'required' }],
  }, ...options }
}

export const useDescriptionColumn = (options) => {
  const i18n = getI18n()
  const webSiteStore = useWebSiteStore()

  const defaultOptions = {
    caption: i18n.t('common.description'),
    dataField: 'description',
    validationRules: options?.validationRules ||
    [{
      type: 'custom',
      validationCallback: (e) => {
        if (e.data.document_row_type) {
          return !!e.value || !(e.data.document_row_type === webSiteStore.documentRowTypesEnum.PRODUCT || e.data.document_row_type === webSiteStore.documentRowTypesEnum.LABOR)
        }
        return !!e.value
      },
      message: i18n.t('common.required'),
      reevaluate: true,
    }],
  }

  return {...defaultOptions, ...options}
}

export const useDiscountColumn = (options) => {
  const i18n = getI18n()
  const { updateTotalNoVatValue, updateTotalVatValue, updateTotalDiscountValue } = useProductService()

  return {...{
    caption: i18n.t('common.discount'),
    ...usePercentageOptions(),
    dataField: 'discount',
    dataType: 'number',
    setCellValue: (newData, value, currentRowData) => {
      newData.discount = value
      updateTotalNoVatValue(newData, currentRowData, options?.includesVat)
      updateTotalVatValue(newData, currentRowData, options?.includesVat)
      updateTotalDiscountValue(newData, currentRowData)
    }
  }, ...options }
}

export const useDiscount2Column = (options) => {
  const i18n = getI18n()
  const { updateTotalNoVatValue, updateTotalVatValue, updateTotalDiscountValue } = useProductService()

  return {
    caption: `${i18n.t('common.discount')} 2`,
    ...usePercentageOptions(),
    dataField: 'discount_2',
    dataType: 'number',
    visible: false,
    setCellValue: (newData, value, currentRowData) => {
      newData.discount_2 = value
      updateTotalNoVatValue(newData, currentRowData, options?.includesVat)
      updateTotalVatValue(newData, currentRowData, options?.includesVat)
      updateTotalDiscountValue(newData, currentRowData)
    }
  }
}

export const useDiscount3Column = (options) => {
  const i18n = getI18n()
  const { updateTotalNoVatValue, updateTotalVatValue, updateTotalDiscountValue } = useProductService()

  return {
    caption: `${i18n.t('common.discount')} 3`,
    ...usePercentageOptions(),
    dataField: 'discount_3',
    dataType: 'number',
    visible: false,
    setCellValue: (newData, value, currentRowData) => {
      newData.discount_3 = value
      updateTotalNoVatValue(newData, currentRowData, options?.includesVat)
      updateTotalVatValue(newData, currentRowData, options?.includesVat)
      updateTotalDiscountValue(newData, currentRowData)
    }
  }
}

export const useDiscountCategoryColumn = (options) => {
  const i18n = getI18n()
  const registryStore = useRegistryStore()

  return {...{
    caption: i18n.t('common.discountCategoryAlt'),
    dataField: 'discount_category_ref',
    lookup: useLookup({
      dataSource: {
        store: registryStore.getLocalizedDiscountCategories(),
        paginate: true,
        pageSize: 25,
      },
      showClearButton: true,
      displayExpr: codeNameDisplayExpr,
      sort: 'name',
    }),
    editorOptions: {
      itemTemplate: (itemData, _, itemElement) => {
        if (!itemData) {
          return
        }
        itemElement.textContent = itemData.name
        itemElement.classList.toggle('dx-disabled-row', itemData.is_locked)
      },
    },
  }, ...options }
}

export const useDocumentProgressStatusColumn = (options) => {
  const i18n = getI18n()
  const garageStore = useGarageStore()
  const portalStore = usePortalStore()

  const dataField = options?.dataField || 'document_progress_status'

  const defaultOptions = {
    dataField: dataField,
    editCellTemplate: 'documentProgressStatus',
    cellTemplate: 'documentProgressStatusCellTemplate',
    lookup: useLookup({
      dataSource: garageStore.getLocalizedDocumentProgressStatuses().filter(i => i.type !== portalStore.documentProgressStatusesEnum.PLANNED),
    })
  }

  return {...{
    caption: i18n.t('common.documentProgressStatus'),
    setCellValue: async(newData, value, currentData) => {
      newData[dataField || 'document_progress_status'] = value
    },
    validationRules: [{ type: 'required' }]
  }, ...defaultOptions, ...options }
}

export const useInterventionTypeColumn = (options) => {
  const i18n = getI18n()
  const garageStore = useGarageStore()

  const dataField = options?.dataField || 'intervention_type_ref'

  const defaultOptions = {
    dataField: dataField,
    editCellTemplate: 'interventionTypeEditCellTemplate',
    lookup: useLookup({ store: garageStore.getLocalizedInterventionTypes() }),
    editorOptions: { placeholder: '' },
  }

  return {...{
    caption: i18n.t('common.interventionType'),
  }, ...defaultOptions, ...options }
}

export const useDocumentRowTypeColumn = (options) => {
  const i18n = getI18n()
  const webSiteStore = useWebSiteStore()
  const { setDocumentRowTypeValue } = useProductService()

  const defaultOptions = {
    caption: i18n.t('common.documentRowType'),
    dataField: 'document_row_type',
    lookup: useLookup({
      store: webSiteStore.getLocalizedDocumentRowTypes(),
    }),
    width: '6rem',
    setCellValue: (newData, value, currentRowData) => setDocumentRowTypeValue(newData, value, currentRowData, options),
    validationRules: [{ type: 'required' }]
  }

  return { ...defaultOptions, ...options }
}

export const useEmailColumn = (options) => {
  const i18n = getI18n()

  return { ...{
    caption: i18n.t('common.email'),
    dataField: 'email',
    showInColumnChooser: false,
    visible: false,
    validationRules: [{ type: 'email' }],
  }, ...options }
}

export const useEngineSizeColumn = (options) => {
  const i18n = getI18n()

  return { ...{
    caption: i18n.t('common.cubicCapacityAlt'),
    dataField: 'engine_size',
    dataType: 'number',
    showInColumnChooser: false,
    visible: false,
    validationRules: [
      {
        type: 'custom',
        message: i18n.t('common.mustBeSixDigits'),
        validationCallback(e) {
          const value = String(e.value ?? '')
          if (value === '') { return true }
          return /^\d{1,6}$/.test(value)
        }
      }
    ],
    editorOptions: { onInput(e) {
      if (e.event && e.event.target && e.event.target.value.length > 6) {
        e.event.target.value = e.event.target.value.slice(0, 6)
      }
    }}
  }, ...options }
}

export const useExchangeRateColumn = () => {
  const i18n = getI18n()

  return {
    caption: i18n.t('common.exchangeRate'),
    dataField: 'exchange_rate',
    dataType: 'number',
    showInColumnChooser: false,
    visible: false,
  }
}

export const useExchangeRateDateColumn = () => {
  const i18n = getI18n()

  return {
    caption: `${i18n.t('common.exchangeRate')} ${i18n.t('common.date').toLocaleLowerCase()}`,
    dataField: 'exchange_rate_date',
    dataType: 'date',
    showInColumnChooser: false,
    visible: false,
  }
}

export const useIsMovementColumn = () => {
  const i18n = getI18n()

  return {
    caption: i18n.t('common.isMovement'),
    dataField: 'is_movement',
    dataType: 'boolean',
    visible: false,
  }
}

export const useProcessingColumn = (options) => {
  const i18n = getI18n()

  return {...{
    caption: i18n.t('common.processingTypes.processing'),
    dataField: 'intervention_type_ref',
    editCellTemplate: 'processingEditor',
    visible: false,
    showInColumnChooser: false,
    validationRules: [{ type: 'required' }]
  }, ...options }
}

export const useProcessingParentColumn = () => ({
  dataField: 'processing_parent_id',
  visible: false,
  showInColumnChooser: false,
})

export const useMechanicsColumn = (options) => {
  const i18n = getI18n()

  return {...{
    caption: i18n.t('common.mechanic'),
    dataField: 'mechanics',
    editCellTemplate: 'mechanicsEditor',
    visible: false,
    showInColumnChooser: false,
  }, ...options }
}

export const useProcessingTypeColumn = (options) => {
  const i18n = getI18n()
  const garageStore = useGarageStore()

  return {...{
    caption: i18n.t('common.processingType'),
    dataField: 'processing_type',
    lookup: useLookup({
      dataSource: {
        store: garageStore.getLocalizedProcessingTypes(),
        sort: 'name',
      },
      displayExpr: 'name',
    }),
    cellTemplate: 'processingType',
    validationRules: [{ type: 'required' }]
  }, ...options }
}

export const useNextRevisionDueDateColumn = (options) => {
  const i18n = getI18n()

  return {...{
    caption: i18n.t('common.revisionNextDate'),
    dataField: 'vehicle.next_revision_due_date',
    dataType: 'date',
    visible: false,
    showInColumnChooser: false,
  }, ...options }
}

export const useNotesColumn = (options) => {
  const i18n = getI18n()
  return {
    ...{
      caption: i18n.t('common.note', 2),
      dataField: 'notes',
    }, ...options }
}

export const useNumberMnemonicColumn = (options) => {
  const i18n = getI18n()
  return {
    ...{
      caption: i18n.t('common.number'),
      dataField: 'number_mnemonic',
      dataType: 'number',
      sortOrder: 'desc',
      width: '6rem',
    }, ...options
  }
}

export const usepaidColumn = (options) => {
  const i18n = getI18n()
  return {...{
    caption: i18n.t('common.paid'),
    dataField: 'paid',
    dataType: 'boolean',
  }, ...options }
}

export const usepaidAmountColumn = (options) => {
  const i18n = getI18n()

  const dataField = options?.dataField || 'paid_amount'

  return {...{
    caption: i18n.t('common.paidAmount'),
    dataField: dataField,
    dataType: 'number',
    width: '10rem',
    calculateDisplayValue: (rowData) => useCurrencyColumnFormatter(rowData, dataField, options?.currency?.id || rowData.currency_ref),
    calculateSortValue: dataField,
  }, ...options }
}

export const usePaymentTypeColumn = (options) => {
  const i18n = getI18n()
  const webSiteStore = useWebSiteStore()

  return { ...{
    caption: i18n.t('common.paymentType'),
    dataField: 'payment_type_ref',
    lookup: useLookup({ store: webSiteStore.getLocalizedPaymentTypes() }),
    visible: false,
  }, ...options }
}

export const usePriceColumn = (options) => {
  const i18n = getI18n()
  const { setPriceValue } = useProductService()

  const dataField = options?.dataField || 'price'

  return { ...{
    caption: options?.caption || i18n.t('common.unitPriceNoVat'),
    dataField: dataField,
    dataType: 'number',
    width: '8rem',
    editorOptions: { min: 0 },
    setCellValue: (newData, value, currentRowData) => setPriceValue(newData, value, currentRowData),
    calculateDisplayValue: (rowData) => useCurrencyColumnFormatter(rowData, dataField, options?.currency?.id || rowData.currency_ref),
    calculateSortValue: dataField,
    validationRules: [{ type: 'required' }],
  }, ...options }
}

export const useValuePercentColumn = (options) => {
  const i18n = getI18n()

  return { ...{
    caption: i18n.t('common.valuePercent'),
    dataField: 'value_percent',
    dataType: 'number',
    ...usePercentageOptions(),
  }, ...options }
}

export const useVinColumn = (options) => {
  const i18n = getI18n()

  const dataField = options?.dataField || 'vin'

  return {
    ...{
      dataField: dataField,
      caption: i18n.t('common.chassisCode'),
      cellTemplate: 'serviceHistoryRef',
      alignment: 'left',
      showInColumnChooser: false,
      editorOptions: {
        ...(!options?.hideInfoTooltip && { buttons: [useButtonsInfoTooltipColumn()] }),
      },
      validationRules: [
        {
          type: 'custom',
          reevaluate: true,
          validationCallback: function(e) {
            const plate = e.data?.vehicle?.plate_number
            if (!e.value && !plate) return false
            return true
          },
          message: i18n.t('common.vinOrPlateRequired'),
        },
        {
          type: 'custom',
          reevaluate: true,
          validationCallback: function(e) {
            if (!e.value) return true
            return e.value.length === 17
          },
          message: i18n.t('common.vinLength'),
        }
      ],
    }, ...options
  }
}

export const useVehicleKmColumn = (options) => {
  const i18n = getI18n()
  const registryStore = useRegistryStore()
  const garageStore = useGarageStore()
  const webSiteStore = useWebSiteStore()

  const dataField = options?.dataField || 'vehicle.km'

  const baseValidationRules = [
    {
      type: 'custom',
      reevaluate: true,
      message: i18n.t('error.validation.greater_value'),
      validationCallback: (e) => {
        const vehicleId = e.data?.vehicle?.id
        const previousKm = registryStore.vehicles.find(v => v.id === vehicleId)?.km

        if (e.value == null) { return previousKm == null }

        if (vehicleId && previousKm != null && e.value < previousKm) {
          return false
        }
        return true
      },
    },
  ]

  return {
    ...{
      caption: i18n.t('common.km'),
      dataField: dataField,
      dataType: 'number',
      visible: false,
      showInColumnChooser: false,
      validationRules: [
        ...(options?.additionalValidationRules ?? []),
        ...baseValidationRules,
        {
          type: 'custom',
          message: i18n.t('common.mustBeSixDigits'),
          validationCallback(e) {
            const value = String(e.value ?? '')
            if (value === '') { return true }
            return /^(0|[1-9]\d{0,5})$/.test(value)
          },
        },
        {
          type: 'custom',
          message: i18n.t('common.required'),
          reevaluate: true,
          validationCallback: (e) => {
            const intervention_type_ref = e.data.intervention_type_ref || garageStore.getLocalizedGarageReasons().find(gr => gr.id === e.data.garage_reason_ref)?.intervention_type_ref

            if (intervention_type_ref === garageStore.getLocalizedInterventionTypes().find(el => el.code === webSiteStore.interventionTypeCodeEnum.CAR_SERVICE)?.id) {
              return e.value != null && e.value !== ''
            }

            return true
          }
        }
      ],
      editorOptions: { onInput(e) {
        if (e.event && e.event.target && e.event.target.value.length > 6) {
          e.event.target.value = e.event.target.value.slice(0, 6)
        }
      }}
    }, ...options
  }
}

export const usePriceVatColumn = (options) => {
  const i18n = getI18n()
  const { setPriceValue } = useProductService()
  const webSiteStore = useWebSiteStore()

  return { ...{
    caption: i18n.t('common.unitPriceVat'),
    dataField: 'price_vat',
    dataType: 'number',
    width: '8rem',
    setCellValue: (newData, value, currentRowData) => {
      newData.price_vat = value

      const actualData = { ...currentRowData, ...newData }

      const vat_type_percentage = webSiteStore.vatTypes.find(i => i.id === actualData.vat_type_ref)?.percentage
      const priceValue = fixedDecimal(+(actualData.price_vat / (1 + (vat_type_percentage || 0))), 6)
      setPriceValue(newData, priceValue, currentRowData)
    },
    calculateDisplayValue: (rowData) => useCurrencyColumnFormatter(rowData, 'price_vat', options?.currency?.id || rowData.currency_ref),
  }, ...options }
}

export const useIncludesVatColumn = (options) => {
  const webSiteStore = useWebSiteStore()

  const dataField = options?.dataField || 'includes_vat'

  return {...{
    caption: webSiteStore.blankCaption,
    dataField: dataField,
    editCellTemplate: 'includesVatEditCellTemplate',
    visible: false,
    showInColumnChooser: false,
  }, ...options }
}

export const useCostColumn = (options) => {
  const i18n = getI18n()
  const { setCostValue } = useProductService()

  const dataField = options?.dataField || 'cost'

  return { ...{
    dataField: dataField,
    caption: options?.caption || i18n.t('common.cost'),
    dataType: 'number',
    width: '8rem',
    setCellValue: (newData, value, currentRowData) => setCostValue(newData, value, currentRowData),
    calculateDisplayValue: (rowData) => useCurrencyColumnFormatter(rowData, dataField, options?.currency?.id || rowData.currency_ref),
    calculateSortValue: dataField,
    validationRules: [{ type: 'required' }],
  }, ...options }
}

export const useCostVatColumn = (options) => {
  const i18n = getI18n()
  const { setCostValue } = useProductService()
  const webSiteStore = useWebSiteStore()
  return { ...{
    caption: i18n.t('common.costVat'),
    dataField: 'cost_vat',
    dataType: 'number',
    width: '10rem',
    setCellValue: (newData, value, currentRowData) => {
      newData.cost_vat = value

      const actualData = { ...currentRowData, ...newData }

      const vat_type_percentage = webSiteStore.vatTypes.find(i => i.id === actualData.vat_type_ref)?.percentage
      const costValue = fixedDecimal(+(actualData.cost_vat / (1 + (vat_type_percentage || 0))), 6)
      setCostValue(newData, costValue, currentRowData)
    },
    calculateDisplayValue: (rowData) => useCurrencyColumnFormatter(rowData, 'cost_vat', options?.currency?.id || rowData.currency_ref),
    calculateSortValue: 'cost_vat',
  }, ...options }
}

export const applyEditorLabel = (editor, text) => {
  const editorElement = editor?.element()
  if (!text || !editorElement) return
  const labelElement = editorElement.closest('.dx-field-item')?.querySelector('.dx-field-item-label-text')
  if (!labelElement) return
  labelElement.textContent = labelElement.textContent.endsWith(':') ? `${text}:` : text
}

export const updateEditorLabels = (editors, fields, buildLabel) => {
  fields.forEach((field) => {
    const item = { dataField: field }
    buildLabel(item)
    applyEditorLabel(editors[field], item.label?.text)
  })
}

export const registerLabelEditors = (e, editors, fields) => {
  if (e.parentType !== 'dataRow' || !fields.includes(e.dataField)) return
  const dataField = e.dataField
  const defaultInitialized = e.editorOptions.onInitialized
  e.editorOptions.onInitialized = (args) => {
    defaultInitialized?.(args)
    editors[dataField] = args.component
  }
}

export const useLabelController = (options) => {
  const registryStore = useRegistryStore()
  const i18n = getI18n()
  const { isVatCountryIta, isVatNumberMandatory, isNameExtMandatory } = useSubjectService()

  const currentVehicleFuelTypeComputed = computed(() => {
    return options?.vehicleFuelType.value
  })

  const currentSubjectType = computed(() => {
    return options?.subjectType.value ? registryStore.getLocalizedSubjectTypes().find(x => x.id === options?.subjectType.value) : null
  })

  const setLabel = (item, trueLabel, falseLabel, condition = null, field = null) => {
    if (field === 'engine_size') {
      currentVehicleFuelTypeComputed.value ? item.label = { text: i18n.t(currentVehicleFuelTypeComputed.value === registryStore.vehicleFuelTypesEnum.ELECTRIC ? trueLabel : falseLabel) } : null
    } else {
      currentSubjectType.value ? item.label = { text: i18n.t(currentSubjectType.value[condition] ? trueLabel : falseLabel) } : null
    }
  }

  const customizeItem = (item, field) => {
    if (item && (item.dataField === 'vehicle.engine_size' || item.dataField === 'engine_size')) {
      setLabel(item, 'common.kiloWattAlt', 'common.cubicCapacityAlt', null, 'engine_size')
    }
    if (item && item.dataField === 'customer.name_ext') {
      setLabel(item, 'common.nameExtAlt', 'common.firstName', 'has_vat_number')
      if (item.label?.text && options?.subjectType && isNameExtMandatory(options.subjectType.value)) {
        item.label = { text: `${item.label.text} *` }
      }
    }
    if (item && item.dataField === 'customer.name') {
      setLabel(item, 'common.nameAlt', 'common.lastName', 'has_vat_number')
    }
    if (item && item.dataField === 'customer.subject_sub_type_ref') {
      setLabel(item, 'common.category', 'common.gender', 'has_vat_number')
    }
    if (item && item.dataField === 'customer.vat_number' && options?.subjectSubTypeId && options?.vatCountryId) {
      if (isVatNumberMandatory(options.subjectSubTypeId.value, options.vatCountryId.value)) {
        item.label = { text: `${i18n.t('common.vatNumber')} *` }
      } else {
        item.label = { text: i18n.t('common.vatNumber') }
      }
    }
    if (item && item.dataField === 'customer.tax_code' && options?.vatCountryId) {
      if (isVatCountryIta(options.vatCountryId.value)) {
        item.label = { text: i18n.t('common.taxCode') }
      } else {
        item.label = { text: i18n.t('common.fiscalId') }
      }
    }
  }

  const updateFormLabels = (editors, fields) => updateEditorLabels(editors, fields, customizeItem)

  return { customizeItem, updateFormLabels, registerLabelEditors }
}

export const usePlateNumberColumn = (options) => {
  const i18n = getI18n()
  const registryStore = useRegistryStore()

  const validatePlateNumberNoSpaces = (e) => {
    if (registryStore.companyData.vehicle_plate_allow_blank_spaces) {
      return true
    }

    const value = e.value || ''
    return !/\s/.test(value)
  }

  return { ...{
    caption: i18n.t('common.plate'),
    dataField: 'vehicle.plate_number',
    visible: false,
    showInColumnChooser: false,
    editorOptions: {
      maxLength: 7,
      ...(!options?.hideInfoTooltip && { buttons: [useButtonsInfoTooltipColumn()] }),
    },
    validationRules: [
      {
        type: 'custom',
        reevaluate: true,
        validationCallback: function(e) {
          const vin = e.data?.vehicle?.vin
          if (!e.value && !vin) return false
          return true
        },
        message: i18n.t('common.vinOrPlateRequired'),
      },
      {
        type: 'custom',
        reevaluate: true,
        validationCallback: validatePlateNumberNoSpaces,
        message: i18n.t('error.validation.noBlankSpacesAllowed'),
      }
    ]
  }, ...options }
}

export const useProductFamilyColumn = (options) => {
  const i18n = getI18n()
  const registryStore = useRegistryStore()

  return { ...{
    caption: i18n.t('common.productFamily'),
    dataField: 'product_family_ref',
    width: '8rem',
    lookup: useLookup({ store: registryStore.getLocalizedProductFamilies() }),
  }, ...options }
}

export const useProductColumns = (options) => {
  const i18n = getI18n()
  const webSiteStore = useWebSiteStore()
  const registryStore = useRegistryStore()
  const { setProductValue } = useProductService()

  const defaultOptions = {
    validationRules: [{
      type: 'custom',
      validationCallback: (e) => {
        return (
          !!e.value ||
          (
            e.data?.document_row_type !== webSiteStore.documentRowTypesEnum.PRODUCT &&
            e.data?.document_row_type !== webSiteStore.documentRowTypesEnum.LABOR &&
            e.data?.document_row_type !== webSiteStore.documentRowTypesEnum.SERVICE
          )
        )
      },
      message: i18n.t('common.required'),
      reevaluate: true,
    }],
  }

  options = { ...defaultOptions, ...options }
  const dataFieldCode = options?.dataFieldCode || 'product_code'

  return [
    {
      caption: i18n.t('common.product'),
      dataField: 'product_ref',
      dataType: 'number',
      editCellTemplate: 'productEditor',
      setCellValue: (newData, value, currentRowData) => {
        const skipVatUpdate = currentRowData.vat_type_ref || !!options?.customer?.value?.vat_type_ref || false
        if (value) {
          newData.product_cost = value.cost
          if (options?.supplierBrand) {
            options.supplierBrand.value = registryStore.supplierBrands.find(i => i.id === value.supplier_brand_ref)
          }
          if (options?.supersessionId) {
            options.supersessionId.value = value.id
          }
          if (value.product_type === webSiteStore.documentRowTypesEnum.LABOR) {
            newData.quantity_ul = 1
            newData.quantity_minutes = value.min_per_ul
          }
        } else if (options?.keepDescription) {
          options.keepDescription.value = false
        }

        if (options?.productUnitOfMeasure) {
          options.productUnitOfMeasure.value = value?.unit_of_measure_ref || null
        }
        setProductValue(newData, value, currentRowData, false, false, skipVatUpdate, { ...options })
      },
      showInColumnChooser: false,
      validationRules: options?.validationRules,
      visible: false,
    },
    {
      caption: i18n.t('common.product'),
      dataField: dataFieldCode,
    },
  ]
}

export const useQuantityColumn = (options) => {
  const i18n = getI18n()
  const { setQuantityValue } = useProductService()

  const caption = options?.caption || i18n.t('common.quantity')
  const dataField = options?.dataField || 'quantity'
  const isQuantity = dataField === 'quantity'

  const defaultSetCellValue = isQuantity
    ? (newData, value, currentRowData) => setQuantityValue(newData, value, currentRowData)
    : (newData, value) => { newData[dataField] = value }
  const setCellValue = options?.setCellValue || defaultSetCellValue

  return {
    caption,
    dataField,
    dataType: 'number',
    width: '5rem',
    setCellValue,
    validationRules: options?.validationRules ?? (isQuantity ? [{ type: 'required' }] : []),
  }
}

export const useReferenceDocumentColumn = (options) => {
  const defaultOptions = {
    alignment: 'left',
    filterOperations: ['contains'],
    selectedFilterOperation: 'contains',
    dataType: 'string',
  }

  return { ...defaultOptions, ...options }
}

export const useRequestedDeliveryDateColumn = () => {
  const i18n = getI18n()

  return {
    caption: i18n.t('common.requestedDeliveryDate'),
    dataField: 'requested_delivery_date',
    dataType: 'date',
    width: '8rem',
    visible: false,
  }
}

export const useRoundingExpenseColumn = (documentRowProductPurchaseFamily = {}, includesVat = {}) => {
  const i18n = getI18n()

  const { setTotalVatValue } = useProductService()

  const portalStore = usePortalStore()
  const isExpensePercentageFamily = computed(() => documentRowProductPurchaseFamily.value?.code === portalStore.hiddenPurchaseFamilyCodes.EXPENSE_PERCENTAGE)

  const setCellValueRounding = (newData, currentRowData, changedField, value) => {
    const actualRowData = { ...currentRowData, ...newData }
    const numericValue = value || 0
    const documentTotalVat = actualRowData.document_total_vat || 0
    const currentPercentage = actualRowData.rounding_percentage || 0

    newData.discount = newData.discount_2 = newData.discount_3 = 0

    if (changedField === 'rounding') {
      newData.rounding = numericValue
      newData.rounded_total = documentTotalVat + numericValue
      newData.rounding_percentage = isExpensePercentageFamily.value && documentTotalVat ? numericValue / documentTotalVat : currentPercentage
    } else if (changedField === 'rounding_percentage' && isExpensePercentageFamily.value) {
      newData.rounding_percentage = numericValue
      newData.rounding = documentTotalVat * newData.rounding_percentage
      newData.rounded_total = documentTotalVat + newData.rounding
    } else if (changedField === 'rounded_total') {
      newData.rounded_total = numericValue
      newData.rounding = numericValue - documentTotalVat
      newData.rounding_percentage = isExpensePercentageFamily.value && documentTotalVat ? newData.rounding / documentTotalVat : currentPercentage
    }

    if (changedField !== 'rounding_percentage' || isExpensePercentageFamily.value) {
      setTotalVatValue(newData, newData.rounding, currentRowData, includesVat?.value)
    }
  }

  const useRoundingDocumentTotalVatColumn = (options) => {
    return {...{
      caption: i18n.t('common.totalVatInit'),
      dataField: 'document_total_vat',
      dataType: 'number',
      visible: false,
      showInColumnChooser: false,
      calculateDisplayValue: (rowData) => useCurrencyColumnFormatter(rowData, 'document_total_vat', options?.currency || rowData.currency_ref),
      editorOptions: { readOnly: true },
      validationRules: [
        {
          type: 'custom',
          message: i18n.t('error.validation.must_be_different'),
          reevaluate: true,
          validationCallback: (e) => {
            if (e.value == null || e.data?.rounded_total == null) return true
            return e.value !== e.data?.rounded_total
          }
        }
      ]
    }, ...options}
  }

  const useRoundingColumn = (options) => {
    return {...{
      caption: i18n.t('common.rounding'),
      dataField: 'rounding',
      dataType: 'number',
      visible: false,
      showInColumnChooser: false,
      calculateDisplayValue: (rowData) => useCurrencyColumnFormatter(rowData, 'rounding', options?.currency || rowData.currency_ref),
      setCellValue: (newData, value, currentRowData) => {
        setCellValueRounding(newData, currentRowData, 'rounding', value)
      },
      validationRules: [
        {
          type: 'custom',
          message: i18n.t('error.validation.must_be_greater_than_zero'),
          reevaluate: true,
          validationCallback: (e) => {
            if (!isExpensePercentageFamily.value) return true
            if (e.value == null) return true
            return e.value > 0
          }
        }
      ]
    }, ...options}
  }

  const useRoundingPercentageColumn = (options) => {
    return {...{
      caption: i18n.t('common.percentage'),
      dataField: 'rounding_percentage',
      dataType: 'number',
      visible: false,
      showInColumnChooser: false,
      setCellValue: (newData, value, currentRowData) => {
        setCellValueRounding(newData, currentRowData, 'rounding_percentage', value)
      },
      validationRules: [
        {
          type: 'custom',
          message: i18n.t('error.validation.must_be_greater_than_zero'),
          reevaluate: true,
          validationCallback: (e) => {
            if (!isExpensePercentageFamily.value) return true
            if (e.value == null) return true
            return e.value > 0
          }
        }
      ]
    }, ...options}
  }

  const useRoundedTotalColumn = (options) => {
    return {...{
      caption: i18n.t('common.totalVat'),
      dataField: 'rounded_total',
      dataType: 'number',
      visible: false,
      showInColumnChooser: false,
      calculateDisplayValue: (rowData) =>
        useCurrencyColumnFormatter(rowData, 'rounded_total', options?.currency || rowData.currency_ref),
      setCellValue: (newData, value, currentRowData) => {
        setCellValueRounding(newData, currentRowData, 'rounded_total', value)
      },
      validationRules: [
        {
          type: 'custom',
          message: i18n.t('error.validation.must_be_different'),
          reevaluate: true,
          validationCallback: (e) => {
            if (e.value == null || e.data?.document_total_vat == null) return true
            return e.value !== e.data?.document_total_vat
          }
        },
        {
          type: 'custom',
          message: i18n.t('error.validation.must_be_greater_than_zero'),
          reevaluate: true,
          validationCallback: (e) => {
            if (!isExpensePercentageFamily.value) return true
            if (e.value == null) return true
            return e.value > 0
          }
        }
      ]
    }, ...options}
  }

  return { useRoundingDocumentTotalVatColumn, useRoundingColumn, useRoundingPercentageColumn, useRoundedTotalColumn }
}

export const useRowNumberColumn = (options) => {
  const i18n = getI18n()

  const defaultOptions = {
    caption: i18n.t('common.rowNumberAlt'),
    width: '5rem',
    dataField: 'row_number',
    dataType: 'number',
    sortOrder: 'asc'
  }

  return { ...defaultOptions, ...options }
}

export const useDocumentTypeColumn = (options) => {
  const i18n = getI18n()
  const portalStore = usePortalStore()

  return {
    ...{
      caption: i18n.t('common.documentType'),
      dataField: 'document_type',
      cellTemplate: 'documentTypeCellTemplate',
      lookup: useLookup({ store: portalStore.getLocalizedDocumentTypes() }),
    }, ...options
  }
}

export const useSaleDocumentTypeColumn = (options) => {
  const i18n = getI18n()
  const portalStore = usePortalStore()

  return {
    ...{
      caption: i18n.t('common.documentType'),
      dataField: 'sale_document_type',
      cellTemplate: 'documentTypeCellTemplate',
      lookup: useLookup({ store: portalStore.getLocalizedDocumentTypes().filter(i => options?.currentSaleDocumentPage?.value ? i.page === options.currentSaleDocumentPage.value : i.page !== null) }),
    }, ...options
  }
}

export const useSaleReasonRefColumn = (options) => {
  const i18n = getI18n()
  const saleStore = useSaleStore()

  return {
    ...{
      caption: i18n.t('common.reason'),
      dataField: 'sale_reason_ref',
      lookup: useLookup({ store: saleStore.getLocalizedSaleReasons() }),
    }, ...options
  }
}

export const useShippingCostTypeColumn = (options) => {
  const i18n = getI18n()
  const portalStore = usePortalStore()

  return {
    ...{
      caption: i18n.t('common.shippingCostType'),
      dataField: 'shipping_cost_type',
      lookup: useLookup({ store: portalStore.getLocalizedShippingCostTypes() }),
    }, ...options
  }
}

export const useSupplierColumn = (options) => {
  const i18n = getI18n()
  const registryStore = useRegistryStore()

  const dataSource = useODataSource(registryStore.api.getAbsoluteUrl() + registryStore.api.suppliersUrl)

  return {...{
    caption: i18n.t('common.supplier'),
    dataField: 'supplier_ref',
    lookup: useLookup({
      dataSource: {
        store: dataSource.store(),
        sort: 'code',
      }
    }),
    setCellValue: async(newData, value) => {
      const id = value?.id || value
      newData[options?.dataField || 'supplier_ref'] = id

      if (typeof value !== 'object') {
        value = (await dataSource.store().byKey(id))?.[0]
      }

      if (value) {
        newData.supplier_brand_ref = value?.supplier_brand_ref || registryStore.getDefaultSupplierBrand()?.id
      }

      newData.supplier_division_ref = null
    },
    validationRules: [{ type: 'required' }]
  }, ...options}
}

export const useSupplierDivisionColumn = (options) => {
  const i18n = getI18n()

  const registryStore = useRegistryStore()

  const dataSource = useODataSource(registryStore.api.getAbsoluteUrl() + registryStore.api.supplierDivisionsUrl)

  const getDataSource = () => {
    return {
      store: dataSource.store(),
      filter: options?.supplierRef?.value ? ['supplier_ref', '=', options.supplierRef.value] : null,
      sort: 'code',
    }
  }

  return {...{
    caption: i18n.t('common.division'),
    dataField: 'supplier_division_ref',
    lookup: useLookup({
      dataSource: getDataSource
    }),
    setCellValue: async(newData, value) => {
      const id = value?.id || value
      newData[options?.dataField || 'supplier_division_ref'] = id

      let entity = value
      if (typeof entity !== 'object') {
        entity = (await dataSource.store().byKey(id))?.[0]
      }

      if (entity) {
        if (value.brand_behavior === registryStore.supplierDivisionBrandBehavior.DIV_SUP ||
          value.brand_behavior === registryStore.supplierDivisionBrandBehavior.DIV_DIV
        ) {
          newData.supplier_brand_ref = entity?.supplier_brand_ref
        } else {
          newData.supplier_brand_ref = registryStore.suppliers.find(i => i.id === entity.supplier_ref)?.supplier_brand_ref
        }
      }
    }}, ...options }
}

export const useInfoBikeBrandColumn = (options) => {
  const i18n = getI18n()
  const registryStore = useRegistryStore()

  const getInfoBikeSupplierBrands = () => registryStore.supplierBrands.filter(b => !!b.infoBike_brand_code)

  const dataSource = new CustomStore({
    key: 'infoBike_brand_code',
    loadMode: 'raw',
    load: async() => getInfoBikeSupplierBrands(),
    byKey: async(key) => {
      const brands = getInfoBikeSupplierBrands()
      return brands.find(b => b.infoBike_brand_code === key)
    }
  })

  const defaultOptions = {
    dataField: 'infoBike.brand',
    lookup: useLookup({
      valueExpr: 'infoBike_brand_code',
      displayExpr: codeNameDisplayExpr,
      searchExpr: ['code', 'name'],
      dataSource: { store: dataSource }
    }),
  }

  return {...{
    caption: i18n.t('common.brandAlt'),
  }, ...defaultOptions, ...options }
}

export const useInfoBikeModelColumn = ({ brand_code, ...options }) => {
  const i18n = getI18n()
  const registryStore = useRegistryStore()

  const store = new CustomStore({
    key: 'ref',
    load: async() => {
      if (!brand_code?.value) return []
      return await registryStore.fetchInfoBikeModelsAsync(brand_code.value)
    },
    byKey: async(key) => {
      if (!brand_code?.value) return null
      const models = await registryStore.fetchInfoBikeModelsAsync(brand_code.value)
      return models.find(m => m.ref === key)
    }
  })

  const defaultOptions = {
    dataField: 'infoBike.model',
    lookup: useLookup({
      valueExpr: 'ref',
      displayExpr: 'name',
      dataSource: { store }
    }),
  }

  return {...{
    caption: i18n.t('common.model'),
  }, ...defaultOptions, ...options }
}

export const useInfoBikeVariantColumn = ({ model_code, ...options }) => {
  const i18n = getI18n()
  const registryStore = useRegistryStore()

  const store = new CustomStore({
    key: 'ref',
    load: async() => {
      if (!model_code?.value) return []
      return await registryStore.fetchInfoBikeVariantsAsync(model_code.value)
    },
    byKey: async(key) => {
      if (!model_code?.value) return null
      const models = await registryStore.fetchInfoBikeVariantsAsync(model_code.value)
      return models.find(m => m.ref === key)
    }
  })

  const defaultOptions = {
    dataField: 'infoBike.variant',
    lookup: useLookup({
      valueExpr: 'ref',
      displayExpr: 'name',
      dataSource: { store }
    }),
  }

  return {...{
    caption: i18n.t('common.variant'),
  }, ...defaultOptions, ...options }
}

export const useSupplierBrandColumn = (options) => {
  const i18n = getI18n()
  const registryStore = useRegistryStore()
  const webSiteStore = useWebSiteStore()

  const getFilteredSupplierBrands = () => {
    const store = (options?.all || isNullOrUndefined(options?.documentRowType?.value) || options?.documentRowType?.value?.type === webSiteStore.documentRowTypesEnum.FREE) ? registryStore.supplierBrands : registryStore.supplierBrands
    return {
      store: options?.inverted ? store.map(i => ({ ...i, id: -i.id })) : store,
      sort: 'code',
      paginate: true,
    }
  }

  const setNestedValue = (obj, path, value) => {
    const keys = path.split('.')
    let current = obj
    keys.forEach((key, index) => {
      if (index === keys.length - 1) {
        current[key] = value
      } else {
        if (!obj[key]) {
          current[key] = {}
        }
        current = current[key]
      }
    })
  }

  const defaultOptions = {
    dataField: 'supplier_brand_ref',
    lookup: useLookup({
      dataSource: getFilteredSupplierBrands,
      codeOnly: options?.codeOnly
    }),
  }

  options = { ...defaultOptions, ...options }

  return {...{
    caption: i18n.t('common.supplierBrand'),
    setCellValue: (newData, value, currentRowData) => {
      setNestedValue(newData, options?.dataField, value)
      const currentSupplierBrand = getFilteredSupplierBrands().store.find(i => i.id === value)
      newData.charge_percentage = currentSupplierBrand?.charge_percentage
      newData.product_ref = null
      newData.product_description = null
      if (options?.supplierBrand) {
        options.supplierBrand.value = currentSupplierBrand
      }
    },
    validationRules: options?.validationRules || [{ type: 'custom',
      validationCallback: (e) => {
        if (e.data.document_row_type) {
          return !!e.value || e.data.document_row_type !== webSiteStore.documentRowTypesEnum.PRODUCT
        }
        return !!e.value
      },
      message: i18n.t('common.required'),
      reevaluate: true,
    }]
  }, ... defaultOptions, ...options }
}

export const useSupplierBrandCodeColumn = (options) => {
  const i18n = getI18n()
  const registryStore = useRegistryStore()
  const webSiteStore = useWebSiteStore()

  const getFilteredSupplierBrands = () => {
    const store = (options?.all || isNullOrUndefined(options?.documentRowType?.value) || options?.documentRowType?.value?.type === webSiteStore.documentRowTypesEnum.FREE) ? registryStore.supplierBrands : registryStore.supplierBrands
    return {
      store: options?.inverted ? store.map(i => ({ ...i, id: -i.id })) : store,
      sort: 'code',
      paginate: true,
    }
  }

  const defaultOptions = {
    dataField: 'supplier_brand_code',
    lookup: useLookup({
      dataSource: getFilteredSupplierBrands,
      valueExpr: 'code',
      codeOnly: options?.codeOnly
    }),
  }

  options = { ...defaultOptions, ...options }

  return {...{
    caption: i18n.t('common.supplierBrand'),
  }, ... defaultOptions, ...options }
}

export const useSupplierOrderGroupColumn = (options) => {
  const i18n = getI18n()
  const orderStore = useOrderStore()

  return {...{
    caption: i18n.t('common.supplierOrderGroup'),
    dataField: 'supplier_order_group_ref',
    width: '10rem',
    lookup: useLookup({ store: orderStore.getLocalizedSupplierOrderGroups() }),
  }, ...options }
}

export const useTotalNoVatColumn = (options) => {
  const i18n = getI18n()

  return {...{
    caption: i18n.t('common.totalNoVat'),
    dataField: 'total_no_vat',
    dataType: 'number',
    width: '10rem',
    calculateDisplayValue: (rowData) => useCurrencyColumnFormatter(rowData, rowData.partial_total_no_vat != null ? 'partial_total_no_vat' : 'total_no_vat', options?.currency?.id || rowData.currency_ref),
    calculateSortValue: 'total_no_vat',
  }, ...options}
}

export const useTotalVatColumn = (options) => {
  const i18n = getI18n()
  const { updateTotalNoVatValue, updateTotalVatValue } = useProductService()

  return {...{
    caption: i18n.t('common.totalVat'),
    dataField: 'total_vat',
    dataType: 'number',
    width: '10rem',
    calculateDisplayValue: (rowData) => useCurrencyColumnFormatter(rowData, rowData.partial_total_vat != null ? 'partial_total_vat' : 'total_vat', options?.currency?.id || rowData.currency_ref),
    calculateSortValue: 'total_vat',
    setCellValue: (newData, value, currentRowData) => {
      newData.total_vat = value
      updateTotalNoVatValue(newData, currentRowData)
      updateTotalVatValue(newData, currentRowData)
    },
  }, ...options}
}

export const useTotalVatValueColumn = (options) => {
  const i18n = getI18n()

  return {
    caption: i18n.t('common.vat'),
    dataField: 'total_vat_value',
    dataType: 'number',
    width: '10rem',
    calculateDisplayValue: (rowData) => useCurrencyColumnFormatter(rowData, 'total_vat_value', options?.currency?.id || rowData.currency_ref),
    calculateSortValue: 'total_vat_value',
  }
}

export const useUnitOfMeasureColumn = (options) => {
  const i18n = getI18n()
  const webSiteStore = useWebSiteStore()
  const { setPriceValue, updateTimeQuantities } = useProductService()
  const { getConversionRatio } = useUnitOfMeasureService()

  const getFilteredUnitOfMeasures = () => {
    const currentProductUnitOfMeasure = webSiteStore.unitsOfMeasure.find(i => i.id === options?.productUnitOfMeasure?.value)
    const availableConversionIds = currentProductUnitOfMeasure ? [...(webSiteStore.unitConversions[currentProductUnitOfMeasure.code] ?? []).map(i => i.id), currentProductUnitOfMeasure.id] : []

    const store = currentProductUnitOfMeasure ? webSiteStore.unitsOfMeasure.filter(i => availableConversionIds.some(x => x === i.id) || i.code === currentProductUnitOfMeasure.code) : webSiteStore.unitsOfMeasure
    return {
      store: store,
      sort: 'code',
      paginate: true,
    }
  }

  return {...{
    caption: i18n.t('common.unitOfMeasure'),
    dataField: 'unit_of_measure_ref',

    lookup: useLookup({
      dataSource: getFilteredUnitOfMeasures,
      codeOnly: options?.codeOnly
    }),
    validationRules: options?.validationRules ?? [],
    editorOptions: {
      showClearButton: options?.showClearButton ?? true,
    },
    async setCellValue(newData, value, currentRowData) {
      options.productUnitOfMeasure.value = value
      newData.unit_of_measure_ref = value
      const ratio = getConversionRatio(currentRowData.unit_of_measure_ref, value, currentRowData.supplier_brand_ref)
      const newPrice = fixedDecimal(+(currentRowData.price * (1 / ratio)))
      setPriceValue(newData, newPrice, currentRowData, false, true, false)
      updateTimeQuantities(newData, currentRowData)
    },
  }, ...options }
}

export const useUrgencyTypeColumn = () => {
  const i18n = getI18n()
  const webSiteStore = useWebSiteStore()

  return {
    caption: i18n.t('common.urgency'),
    dataField: 'urgency_type_ref',
    lookup: useLookup({ store: webSiteStore.getLocalizedUrgencyTypes() }),
  }
}

export const useUrgencyDiscountColumn = () => {
  const i18n = getI18n()
  const { updateTotalNoVatValue, updateTotalVatValue } = useProductService()

  return {
    caption: i18n.t('common.urgencyDiscount'),
    ...usePercentageOptions(),
    dataField: 'urgency_discount',
    dataType: 'number',
    visible: false,
    setCellValue: (newData, value, currentRowData) => {
      newData.urgency_discount = value
      updateTotalNoVatValue(newData, currentRowData)
      updateTotalVatValue(newData, currentRowData)
    }
  }
}

export const useUrgencyDiscount2Column = () => {
  const i18n = getI18n()
  const { updateTotalNoVatValue, updateTotalVatValue } = useProductService()

  return {
    caption: `${i18n.t('common.urgencyDiscount')} 2`,
    ...usePercentageOptions(),
    dataField: 'urgency_discount_2',
    dataType: 'number',
    visible: false,
    setCellValue: (newData, value, currentRowData) => {
      newData.urgency_discount_2 = value
      updateTotalNoVatValue(newData, currentRowData)
      updateTotalVatValue(newData, currentRowData)
    }
  }
}

export const useVehicleNotesColumn = (options) => {
  const i18n = getI18n()

  return {
    ...{
      caption: i18n.t('common.vehicleNotes'),
      dataField: 'vehicle_notes',
      visible: false,
    }, ...options
  }
}

export const useVehicleColumn = (options) => {
  const i18n = getI18n()
  const registryStore = useRegistryStore()

  const getFilteredVehicles = () => {
    const store = registryStore.vehicles
    return {
      store: store,
      sort: 'code',
      paginate: true,
    }
  }
  const dataField = options?.dataField || 'vehicle'

  const defaultOptions = {
    dataField: dataField,
    lookup: useLookup({
      dataSource: getFilteredVehicles,
    })
  }

  let optSetCellValue = null
  if (options?.setCellValue) {
    optSetCellValue = options.setCellValue
    delete options.setCellValue
  }

  return {...{
    caption: i18n.t('common.vehicle'),
    setCellValue: async(newData, value, currentData) => {
      const id = value?.id || value
      newData[dataField || 'vehicle'] = id

      if (optSetCellValue) {
        await optSetCellValue(newData, value, currentData)
        options?.formInstance?.value?.repaint()
      }
    },
    validationRules: [{ type: 'required' }]
  }, ...defaultOptions, ...options }
}

export const useVatTypeColumn = (options) => {
  const i18n = getI18n()
  const webSiteStore = useWebSiteStore()
  const { setVatTypeValue } = useProductService()

  const store = options?.exemption_vat_type ? webSiteStore.getLocalizedVatTypes().filter(v => v.stamp_required) : webSiteStore.getLocalizedVatTypes()

  const defaultOptions = {
    caption: i18n.t('common.vat'),
    dataField: 'vat_type_ref',
    lookup: useLookup({ store: store }),
    setCellValue: (newData, value, currentRowData) => setVatTypeValue(newData, value, currentRowData),
    validationRules: [{
      type: 'custom',
      validationCallback: (e) => {
        return !!e.value || e.data.document_row_type === webSiteStore.documentRowTypesEnum.TEXT || (!e.data.price && e.data.document_row_type === webSiteStore.documentRowTypesEnum.FREE)
      },
      message: i18n.t('common.required'),
      reevaluate: true,
    }]
  }

  return {...defaultOptions, ...options }
}

export const useVatTypePercentageColumn = (options) => {
  return { ...{
    dataField: 'vat_type_percentage',
    ...usePercentageOptions(),
    dataType: 'number',
  }, ...options }
}

export const useVatTypeColumns = (options) => {
  return [
    useVatTypeColumn(options),
    useVatTypePercentageColumn(options)
  ]
}

export const useCustomerVatNumberColumn = (options) => {
  const i18n = getI18n()
  const registryStore = useRegistryStore()
  const { validateVatNumber, validateRequiredVatNumber } = useValidation()

  return {
    ...{
      caption: i18n.t('common.vatNumber'),
      dataField: 'customer.vat_number',
      validationRules: [{
        type: 'custom',
        reevaluate: true,
        validationCallback: (e) => !registryStore.companyData.subject_validate_vat_and_fiscal_code || validateVatNumber(e.value, e.data?.customer?.vat_country_id),
      },
      {
        type: 'custom',
        message: i18n.t('common.required'),
        reevaluate: true,
        validationCallback: (e) => {
          return validateRequiredVatNumber(e.value, e.data?.customer?.vat_country_id, e.data?.customer?.subject_sub_type_ref)
        }
      }],
      visible: false,
      showInColumnChooser: false
    }, ...options
  }
}

export const useTaxCodeColumn = (options) => {
  const i18n = getI18n()
  const dataField = options?.dataField || 'tax_code'

  return {
    ...{
      caption: i18n.t('common.taxCode'),
      dataField: dataField,
      setCellValue: (newData, value) => {
        setByPath(newData, dataField, value?.toUpperCase())
      },
    }, ...options
  }
}

export const useSeriesColumn = (options) => {
  const i18n = getI18n()

  return {...{
    caption: i18n.t('common.series'),
    dataField: 'series',
    dataType: 'string',
    sortOrder: 'desc',
    width: '6rem',
  }, ...options }
}

export const useDateColumn = (options) => {
  const i18n = getI18n()
  return {...{
    dataField: 'date',
    caption: i18n.t('common.date'),
    dataType: 'date',
    width: '8rem',
  }, ...options }
}

export const useDocumentDateColumn = (options) => {
  return useDateColumn({
    editorOptions: { max: new Date() },
    caption: options.caption || getI18n().t('common.date'),
    ...options,
  })
}

const utcToLocalDate = (value) => {
  return value instanceof Date ? value : moment.utc(value).local().toDate()
}

export const useDateTimeColumn = (options) => {
  const i18n = getI18n()

  const dataField = options?.dataField || 'created_at'

  return {...{
    dataField: dataField,
    caption: i18n.t('common.createdAt'),
    dataType: 'datetime',
    width: '10rem',
    calculateCellValue: rowData => rowData?.[dataField] ? utcToLocalDate(rowData[dataField]) : '',
  }, ...options }
}

export const useTimeColumn = (options) => {
  const i18n = getI18n()
  const dataField = options?.dataField || 'created_at'

  return {
    dataField,
    caption: i18n.t('common.time'),
    dataType: 'datetime',
    width: '6rem',
    calculateCellValue: rowData => rowData?.[dataField] ? utcToLocalDate(rowData[dataField]) : '',
    format: 'HH:mm',

    ...options,
  }
}

export const useMultiSelectColumn = (options) => {
  return {...{
    dataType: 'string',
    width: '14rem',
  }, ...options }
}
